import { ILineChartDataConfig } from '@/shared/Charting/LineChart/lineChart.types.ts'
import { useRecoilValue } from 'recoil'
import parametersSelectors from '@/pages/Parameters/parameters.selectors.ts'
import { useMemo } from 'react'
import { IComputedParameterDef, IParameter, TParameterKey } from '@/pages/Parameters/parameters.types.ts'
import objectUtils from '@/utils/object.utils.ts'

interface IDataRefConfigIntoRendererPluginParams {
  initialTsFrom: number
  initialTsTo: number
  devices: number[]
  parameters: TParameterKey[]
  computed?: IComputedParameterDef[]
}

const getComputedSourceId = (computed: IComputedParameterDef) => computed.sourceId || computed.id.replace(/^computed:/, '')

/**
 * Хук, переводящий информацию о конфигурации данных из IUseDataRefProps в ILineChartDataConfig
 * */
const useDataRefConfigIntoRendererPluginParams = (
  params: IDataRefConfigIntoRendererPluginParams,
  parametersConfigs: Array<{ parameterId: IParameter['id']; color: string }>
): ILineChartDataConfig => {
  const parametersMap = useRecoilValue(parametersSelectors.getParametersMap)

  return useMemo(() => {
    const parametersConfigsMap = objectUtils.arrToMap(parametersConfigs, 'parameterId')
    const computed = params.computed ?? []
    const computedById = objectUtils.arrToMap(computed, 'id')

    const parameterColorsBySourceId = parametersConfigs.reduce<Record<string, string>>((acc, config) => {
      const sourceId = parametersMap[config.parameterId]?.sourceId
      if (sourceId) {
        acc[sourceId] = config.color
      }
      return acc
    }, {})

    const selectedNumericSourceIds = new Set(
      params.parameters
        .filter((parameterId): parameterId is IParameter['id'] => typeof parameterId === 'number')
        .map((parameterId) => parametersMap[parameterId]?.sourceId)
        .filter(Boolean)
    )

    return {
      tsFrom: params.initialTsFrom,
      tsTo: params.initialTsTo,
      parameters: params.parameters.reduce<ILineChartDataConfig['parameters']>((acc, parameterId) => {
        if (typeof parameterId === 'number') {
          const parameter = parametersMap[parameterId]
          if (!parameter) return acc

          const linkedComputed = computed.find((item) => getComputedSourceId(item) === parameter.sourceId)
          acc.push({
            id: parameter.id,
            dataParameterId: linkedComputed?.id ?? parameter.id,
            sourceId: parameter.sourceId,
            name: parameter.name,
            dataType: linkedComputed?.dataType ?? parameter.dataType,
            color: parametersConfigsMap[parameter.id]?.color,
          })
          return acc
        }

        const computedParameter = computedById[parameterId]
        if (!computedParameter) return acc

        const sourceId = getComputedSourceId(computedParameter)
        if (selectedNumericSourceIds.has(sourceId)) return acc

        acc.push({
          id: computedParameter.id,
          dataParameterId: computedParameter.id,
          sourceId,
          name: computedParameter.name,
          dataType: computedParameter.dataType || 'FLOAT',
          color: parameterColorsBySourceId[sourceId],
        })

        return acc
      }, []),
      equipmentId: params.devices[0],
    }
  }, [parametersMap, parametersConfigs, params.computed, params.devices, params.initialTsFrom, params.initialTsTo, params.parameters])
}

export default useDataRefConfigIntoRendererPluginParams
